import { searchContent } from '@echo/api-client/onearly';
import type { MessageDescriptor } from '@lingui/core';
import type { StrongerMemoryTypes } from '../config/environment';
import {
  MATH_SUBCATEGORIES,
  type MathCategory,
  type MathLevel,
  type MathSubcategory,
  mathSubcategoryOf,
  pickMathSet,
} from '../domain/math';
import { type ContentGraph, childrenOf, jsonValueOf, readGraph } from './graph';
import {
  type MathProblem,
  mathProblemSchema,
  smLibraryValueSchema,
  smMathPracticeSheetSchema,
  smMathSetSchema,
} from './schemas';
import { nextUnused } from './workbook';

/**
 * The Math library (scripts/seeds/quick-math) as Core serves it: one `SM Library` container
 * holding its rows (`SM Math Set`, a variation such as "Small numbers") in order, each row
 * holding its practice sheets (`SM Math Sheet`). Small enough to read whole in one search.
 */
export const MATH_LIBRARY_HANDLE = 'math/en-US';

/** One practice sheet of a row: what one Quick math exercise works through. */
export interface MathPracticeSheet {
  lineageUuid: string;
  /** The version read. */
  uuid: string;
  sheet: number;
  problems: MathProblem[];
  /** How its row lays sums out: `stacked` (in a column) or `across`; the row's own format. */
  format?: string;
}

export interface MathSet {
  lineageUuid: string;
  /** The version read: what Saved points at. */
  uuid: string;
  key: string;
  /** The variation: "Small numbers". */
  title: string;
  /** `addition`, `subtraction`, `missing-numbers`, `number-patterns` or `visual`. */
  category: string;
  /** `easy`, `medium`, `challenge`; some rows span two. */
  levels: string[];
  /** `stacked`, `across`, `text` or `visual` (the content table's format). */
  format?: string;
  /** The illustration's name ("small-numbers", "dice"). */
  icon?: string;
  summary?: string;
  example?: string;
  /** In order; sheets with no problem the app can show are left out. */
  sheets: MathPracticeSheet[];
}

export interface MathLibrary {
  sets: MathSet[];
}

export const EMPTY_MATH_LIBRARY: MathLibrary = { sets: [] };

/** Types the Math library needs; the environment has it only when all four are set. */
export type MathLibraryTypes = Required<
  Pick<StrongerMemoryTypes, 'library' | 'libraryKey' | 'mathSet' | 'sheet'>
>;

export function mathLibraryTypes(
  types: StrongerMemoryTypes | undefined,
): MathLibraryTypes | undefined {
  if (!types?.library || !types.libraryKey || !types.mathSet || !types.sheet) return;
  return {
    library: types.library,
    libraryKey: types.libraryKey,
    mathSet: types.mathSet,
    sheet: types.sheet,
  };
}

/** The library by its handle, and only the programme group's (as the read-aloud library). */
export async function loadMathLibrary(
  types: MathLibraryTypes,
  groupActorUuid: string | undefined,
): Promise<MathLibrary> {
  const response = await searchContent({
    rootContentTypeUuids: [types.library],
    predicates: [
      {
        pathTypeUuids: [types.libraryKey],
        op: 'eq',
        valueType: 'STRING',
        value: MATH_LIBRARY_HANDLE,
      },
    ],
    ...(groupActorUuid
      ? { actorPredicates: [{ edgeTypes: ['AUTHORED_BY'], actorUuids: [groupActorUuid] }] }
      : {}),
    maxDepth: 2,
    first: 1,
  });
  if (response.status !== 200) {
    throw new Error(`OnEarly: math library search failed (${response.status})`);
  }
  return mathLibraryFromGraph(readGraph(response.data), types);
}

export function mathLibraryFromGraph(graph: ContentGraph, types: MathLibraryTypes): MathLibrary {
  const root = graph.roots[0] ? graph.nodes.get(graph.roots[0]) : undefined;
  if (!root || !smLibraryValueSchema.safeParse(jsonValueOf(root)).success) {
    return EMPTY_MATH_LIBRARY;
  }
  const sets: MathSet[] = [];
  for (const node of childrenOf(graph, root.uuid)) {
    if (node.contentTypeUuid !== types.mathSet) continue;
    const parsed = smMathSetSchema.safeParse(jsonValueOf(node));
    if (!parsed.success || parsed.data.retired) continue;
    const { format } = parsed.data;
    const sheets: MathPracticeSheet[] = [];
    for (const child of childrenOf(graph, node.uuid)) {
      if (child.contentTypeUuid !== types.sheet) continue;
      const sheet = smMathPracticeSheetSchema.safeParse(jsonValueOf(child));
      if (!sheet.success) continue;
      const problems = sheet.data.problems.flatMap((problem) => {
        const known = mathProblemSchema.safeParse(problem);
        return known.success ? [known.data] : [];
      });
      if (problems.length > 0) {
        sheets.push({
          lineageUuid: child.lineageUuid,
          uuid: child.uuid,
          sheet: sheet.data.sheet,
          problems,
          ...(format ? { format } : {}),
        });
      }
    }
    if (sheets.length === 0) continue;
    const { key, title, category, levels, icon, summary, example } = parsed.data;
    sets.push({
      lineageUuid: node.lineageUuid,
      uuid: node.uuid,
      key,
      title,
      category,
      levels,
      sheets,
      ...(format ? { format } : {}),
      ...(icon ? { icon } : {}),
      ...(summary ? { summary } : {}),
      ...(example ? { example } : {}),
    });
  }
  return { sets };
}

/**
 * A row of Library › Math as the app shows it (PRD-2431): every Core row's sheets regrouped
 * by the kind of problem they ask, in the library's order.
 */
export interface MathGroup {
  key: MathSubcategory;
  category: MathCategory;
  title: MessageDescriptor;
  /**
   * What opens and saves it: the first Core row whose first sheet is in it, or else its own
   * first sheet.
   */
  lineageUuid: string;
  /** The version read: what Saved points at. */
  uuid: string;
  /**
   * Every lineage that names it: its own, then the other Core rows whose first sheet is in it,
   * as Saved or an exercise left off may hold them.
   */
  lineages: string[];
  /**
   * A lineage it was saved under before a sheet moved to another row (v1.2.0 filed sheet 12-3
   * in 2-digit subtraction, which opened from it): Saved and the row's info page only, never
   * what opens an exercise, which goes by the sheet's row now.
   */
  savedAs: string[];
  /** Its sheets' rows' levels, easiest first. */
  levels: string[];
  /** The leading row's illustration and summary. */
  icon?: string;
  summary?: string;
  sheets: MathPracticeSheet[];
}

const LEVEL_ORDER = ['easy', 'medium', 'challenge'];
const levelRank = (level: string) => {
  const rank = LEVEL_ORDER.indexOf(level);
  return rank === -1 ? LEVEL_ORDER.length : rank;
};

/**
 * The row a sheet is in, and what it asks there. A sheet of sums goes in the row most of its
 * sums fit and asks only those, because a workbook sheet can run on from 9 − 9 to 10 − 0; it
 * stays in one row so its lineage names one row (Saved, a run left off, sheets done).
 */
function placeSheet(sheet: MathPracticeSheet): [MathSubcategory, MathPracticeSheet] | undefined {
  const key = mathSubcategoryOf(sheet.problems);
  if (!key || sheet.problems[0]?.kind !== 'column') return key ? [key, sheet] : undefined;
  const keys = sheet.problems.map((problem) => mathSubcategoryOf([problem]));
  const counts = new Map<MathSubcategory | undefined, number>();
  for (const each of keys) counts.set(each, (counts.get(each) ?? 0) + 1);
  // Sorting is stable: a tie goes to the row of the sheet's first sum.
  const [row] = [...counts].sort((a, b) => b[1] - a[1])[0] ?? [key];
  if (!row) return undefined;
  return [row, { ...sheet, problems: sheet.problems.filter((_, index) => keys[index] === row) }];
}

/** The library's rows regrouped into the design's; a sheet the app can't place is left out. */
export function groupMathLibrary(library: MathLibrary): MathGroup[] {
  const found = new Map<
    MathSubcategory,
    { rows: MathSet[]; leads: MathSet[]; sheets: MathPracticeSheet[] }
  >();
  // Each row's first sheet as v1.2.0 filed it, by the sheet's largest number.
  const formerFirst = new Map<MathSubcategory, string>();
  for (const set of library.sets) {
    set.sheets.forEach((whole, index) => {
      const former = mathSubcategoryOf(whole.problems);
      if (former && !formerFirst.has(former)) formerFirst.set(former, whole.lineageUuid);
      const placed = placeSheet(whole);
      if (!placed) return;
      const [key, sheet] = placed;
      const group = found.get(key) ?? { rows: [], leads: [], sheets: [] };
      found.set(key, group);
      if (!group.rows.includes(set)) group.rows.push(set);
      if (index === 0) group.leads.push(set);
      group.sheets.push(sheet);
    });
  }
  return MATH_SUBCATEGORIES.flatMap(({ key, category, title }) => {
    const group = found.get(key);
    const [firstSheet] = group?.sheets ?? [];
    if (!group || !firstSheet) return [];
    const lead = group.leads[0] ?? group.rows[0];
    const levels = [...new Set(group.rows.flatMap((row) => row.levels))].sort(
      (a, b) => levelRank(a) - levelRank(b),
    );
    // A row no Core row starts opened from its first sheet, which may since have moved.
    const former = group.leads.length === 0 ? formerFirst.get(key) : undefined;
    return [
      {
        key,
        category,
        title,
        lineageUuid: group.leads[0]?.lineageUuid ?? firstSheet.lineageUuid,
        uuid: group.leads[0]?.uuid ?? firstSheet.uuid,
        lineages:
          group.leads.length > 0
            ? group.leads.map((row) => row.lineageUuid)
            : [firstSheet.lineageUuid],
        savedAs: former && former !== firstSheet.lineageUuid ? [former] : [],
        levels,
        ...(lead?.icon ? { icon: lead.icon } : {}),
        ...(lead?.summary ? { summary: lead.summary } : {}),
        sheets: group.sheets,
      },
    ];
  });
}

/**
 * The row a lineage opens (its own, a Core row's that starts in it, or one of its sheets'),
 * and that sheet when it names one.
 */
export function openMathGroup(
  groups: readonly MathGroup[],
  lineageUuid: string,
): { group: MathGroup; sheet?: MathPracticeSheet } | undefined {
  const named = groups.find((group) => group.lineages.includes(lineageUuid));
  if (named) return { group: named };
  for (const group of groups) {
    const sheet = group.sheets.find((candidate) => candidate.lineageUuid === lineageUuid);
    if (sheet) return { group, sheet };
  }
  return undefined;
}

/**
 * "Pick for me" at `level`, by the Core rows' own levels: the next sheet not done of the row
 * `pickMathSet` chooses, among the sheets Library › Math shows.
 */
export function pickMathSheet(
  library: MathLibrary,
  level: MathLevel,
  used: ReadonlySet<string>,
): MathPracticeSheet | undefined {
  const rows = library.sets.flatMap((set) => {
    const sheets = set.sheets.filter((sheet) => mathSubcategoryOf(sheet.problems) !== undefined);
    return sheets.length > 0 ? [{ ...set, sheets }] : [];
  });
  const row = pickMathSet(rows, level, used);
  return row ? nextUnused(row.sheets, used) : undefined;
}
